// Shared data layer for prep_app.
//
// A tiny SWR-like hook on top of AuthContext.apiFetch. Gives every screen a
// consistent { data, error, loading, refetch, mutate } contract with:
//   • a module-level cache (so the same key is shared across components/tabs),
//   • in-flight de-duplication (one request per key at a time),
//   • short dedupe window (avoids refetch storms on quick remounts),
//   • cross-component sync (mutate/refetch updates every subscriber),
//   • optional optimistic updates via mutate(),
//   • opt-in revalidate-on-focus.
//
// apiFetch from AuthContext is recreated on every render (not memoized) and
// returns `null` on 401, so we read it through a ref and treat null as an
// auth error rather than data.
import { useEffect, useReducer, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext.jsx';

const store = new Map();      // key -> { data, error, isValidating, ts }
const listeners = new Map();  // key -> Set<fn>
const inflight = new Map();   // key -> Promise
const fetchers = new Map();   // key -> () => Promise  (latest registered fetcher)

function emit(key) {
  const set = listeners.get(key);
  if (set) set.forEach((fn) => { try { fn(); } catch { /* noop */ } });
}
function setEntry(key, patch) {
  store.set(key, { ...(store.get(key) || {}), ...patch });
  emit(key);
}
function subscribe(key, fn) {
  let set = listeners.get(key);
  if (!set) { set = new Set(); listeners.set(key, set); }
  set.add(fn);
  return () => { set.delete(fn); if (set.size === 0) listeners.delete(key); };
}

// Run a fetch for `key`, de-duplicating concurrent calls and honouring a small
// dedupe window unless `force` is set.
const gaveUp = new Set(); // ключи, по которым уже была повторная попытка

function runFetch(key, fetcher, { force = false, dedupeMs = 2000 } = {}) {
  if (inflight.has(key)) return inflight.get(key);
  const cur = store.get(key);
  if (!force && cur && cur.ts && cur.data !== undefined && (Date.now() - cur.ts) < dedupeMs) {
    return Promise.resolve(cur.data);
  }
  setEntry(key, { isValidating: true });
  const p = Promise.resolve()
    .then(() => fetcher())
    .then((data) => { setEntry(key, { data, error: undefined, isValidating: false, ts: Date.now() }); return data; })
    .catch((err) => {
      // 502/503/504 = бэкенд перезапускается (обычно деплой). Это не ошибка
      // приложения: пробуем ещё раз через 2.5с, и только потом сдаёмся.
      const st = err && err.status;
      if ((st === 502 || st === 503 || st === 504) && !gaveUp.has(key)) {
        gaveUp.add(key);
        return new Promise((resolve, reject) => setTimeout(() => {
          Promise.resolve().then(() => fetcher())
            .then((data) => { gaveUp.delete(key); setEntry(key, { data, error: undefined, isValidating: false, ts: Date.now() }); resolve(data); })
            .catch((e2) => { gaveUp.delete(key); setEntry(key, { error: e2, isValidating: false, ts: Date.now() }); reject(e2); });
        }, 2500));
      }
      setEntry(key, { error: err, isValidating: false, ts: Date.now() });
      throw err;
    })
    .finally(() => { inflight.delete(key); });
  inflight.set(key, p);
  return p;
}

// ---- imperative API (usable outside React) ----

// Force-revalidate a key that some mounted component is using.
export function revalidate(key) {
  const f = fetchers.get(key);
  return f ? runFetch(key, f, { force: true }).catch(() => {}) : Promise.resolve();
}

// Update a cached value directly (optimistic update). `data` may be a value or
// an updater (prev) => next. Pass { revalidate: true } to refetch afterwards.
export function mutate(key, data, { revalidate: rv = false } = {}) {
  const next = typeof data === 'function' ? data(store.get(key)?.data) : data;
  setEntry(key, { data: next, error: undefined });
  if (rv) revalidate(key);
}

// Drop cached data. No key -> clear everything (used e.g. on error-recovery).
export function clearCache(key) {
  if (key) { store.delete(key); emit(key); }
  else { const keys = [...store.keys()]; store.clear(); keys.forEach(emit); }
}

/**
 * useResource(key, options)
 *   key      string request path (after /api), or null/false to skip.
 *   options  {
 *     enabled            default true; when false, no fetch happens.
 *     fetcher            (apiFetch) => Promise<data>; custom loader.
 *     dedupeMs           default 2000.
 *     revalidateOnFocus  default false.
 *   }
 * returns { data, error, loading, isValidating, refetch, mutate }
 */
export function useResource(key, options = {}) {
  const { enabled = true, fetcher: customFetcher, dedupeMs = 2000, revalidateOnFocus = false } = options;
  const active = !!key && enabled;
  const { apiFetch } = useAuth();
  const apiRef = useRef(apiFetch); apiRef.current = apiFetch;
  const [, rerender] = useReducer((c) => c + 1, 0);

  // default GET-JSON fetcher (or the caller's custom one)
  const fetcher = useCallback(async () => {
    if (customFetcher) return customFetcher(apiRef.current);
    const res = await apiRef.current(key);
    if (res == null) { const e = new Error('Unauthorized'); e.status = 401; throw e; }
    if (!res.ok) { const e = new Error('Request failed: ' + res.status); e.status = res.status; throw e; }
    if (res.status === 204) return null;
    const ct = res.headers.get('content-type') || '';
    return ct.includes('application/json') ? res.json() : res.text();
  }, [key, customFetcher]);
  const fetcherRef = useRef(fetcher); fetcherRef.current = fetcher;

  useEffect(() => {
    if (!active) return undefined;
    fetchers.set(key, () => fetcherRef.current());
    const unsub = subscribe(key, rerender);
    // Ошибка уже сохраняется в состояние — глотаем отклонение, иначе каждый
    // сбой сети (502 при деплое, офлайн) улетал в Sentry необработанным.
    runFetch(key, () => fetcherRef.current(), { dedupeMs }).catch(() => {});
    return unsub;
  }, [key, active, dedupeMs]);

  useEffect(() => {
    if (!active || !revalidateOnFocus) return undefined;
    const onFocus = () => runFetch(key, () => fetcherRef.current(), { force: true }).catch(() => {});
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [key, active, revalidateOnFocus]);

  const refetch = useCallback(() => runFetch(key, () => fetcherRef.current(), { force: true }), [key]);
  const mutateLocal = useCallback((data, opts) => mutate(key, data, opts), [key]);

  const e = active ? (store.get(key) || {}) : {};
  return {
    data: e.data,
    error: e.error,
    loading: active && e.data === undefined && e.error === undefined,
    isValidating: !!e.isValidating,
    refetch,
    mutate: mutateLocal,
  };
}

export default useResource;
